import { type FC, useCallback, useMemo, useState } from "react";

import { MegaphoneIcon } from "@snapchat/snap-design-system-icons";

import { type MyLensesLensCta, MyLensesLensCtaText, MyLensesLensCtaType } from "../../../gql-generated/types";
import { LensCtaChild, LensCtaDisplay, LensCtaMediaPreview, formContainerStyle } from "../../../lens-cta/components";
import { EditLensCtaForm, EditLensCtaFormik } from "../../../lens-cta/components/LensCtaForm";
import { LensCtaTextLabelMap, makeLensCtaForUI } from "../../../lens-cta/models";
import { EditLensCtaFormSchema, type EditLensCtaFormValues } from "../../../lens-cta/validation";
import { Flexbox, FormModal } from "../../../shared/components";
import { useToggleState } from "../../../shared/hooks";
import { type LensPreviewMediaItem } from "../../../shared/state";
import { useLensChangeTracker } from "../../hooks/useLensChangeTracker";
import { lensCtaStrings } from "../../models";
import { FieldLabel } from "../shared";

import { lensCtaContainer } from "./LensCta.styles";
import { SidebarModalHeading, SidebarModalInfo } from "./LensSidebar.styles";
import { SidebarFooterButtons } from "./SidebarFooterButtons";
import { SidebarPanel } from "./SidebarPanel";

interface LensCtaProps {
    disabled: boolean;
    lensPreview: LensPreviewMediaItem;
    lensIconURL: string;
    lensId: string;
}

export const LensCtaContent: FC<LensCtaProps> = ({ disabled, lensPreview, lensIconURL, lensId }) => {
    const { lensCta, updateCta, hasFieldChanged, updateLensLoading } = useLensChangeTracker(lensId);

    const [lensCtaModalProps, showLensCtaModal] = useToggleState();
    const [showErrors, setShowErrors] = useState(false);

    const initialValues: EditLensCtaFormValues = useMemo(
        () =>
            makeLensCtaForUI({
                type: lensCta?.type ?? MyLensesLensCtaType.CtaTypeUnset,
                text: lensCta?.text ?? MyLensesLensCtaText.CtaTextUnset,
                website: {
                    url: lensCta?.website?.url ?? "",
                },
                deeplink: {
                    uri: lensCta?.deeplink?.uri ?? "",
                    appName: lensCta?.deeplink?.appName ?? "",
                    iosAppId: lensCta?.deeplink?.iosAppId ?? 0,
                    androidAppUrl: lensCta?.deeplink?.androidAppUrl ?? "",
                },
            }),
        [lensCta],
    );

    const applyLensCta = useCallback(
        (lensCta: MyLensesLensCta) => {
            updateCta(lensCta);
            lensCtaModalProps.onCancel();
        },
        [updateCta, lensCtaModalProps],
    );

    const handleFormikSubmit = useCallback(
        (values: EditLensCtaFormValues) => {
            const { type, text, deeplink, website } = EditLensCtaFormSchema.cast(values);
            if (type === MyLensesLensCtaType.CtaTypeDeeplink) {
                const { uri = "", appName = "", iosAppId = 0, androidAppUrl = "" } = deeplink;
                applyLensCta({
                    type,
                    text,
                    deeplink: {
                        uri,
                        appName,
                        iosAppId,
                        androidAppUrl,
                    },
                });
            } else if (type === MyLensesLensCtaType.CtaTypeWebsite) {
                const { url = "" } = website;
                applyLensCta({
                    type,
                    text,
                    website: {
                        url,
                    },
                });
            } else {
                applyLensCta({
                    type: MyLensesLensCtaType.CtaTypeUnset,
                    text: MyLensesLensCtaText.CtaTextMore,
                });
            }
        },
        [applyLensCta],
    );

    return (
        <>
            <SidebarPanel
                {...lensCtaStrings}
                label={<FieldLabel hasChanges={hasFieldChanged("lensCta")}>{lensCtaStrings.label}</FieldLabel>}
                Icon={MegaphoneIcon}
                showModal={showLensCtaModal}
                disabled={disabled}
                dataTestIdPrefix="lens-cta"
            >
                {lensCta && lensCta.type !== MyLensesLensCtaType.CtaTypeUnset && (
                    <Flexbox className={lensCtaContainer} flexDirection="row">
                        <LensCtaDisplay lensCta={lensCta} />
                    </Flexbox>
                )}
            </SidebarPanel>

            <EditLensCtaFormik
                enableReinitialize
                initialValues={initialValues}
                onSubmit={handleFormikSubmit}
                validationSchema={EditLensCtaFormSchema}
                validateOnMount={true}
            >
                {({ submitForm, values, isValid }) => (
                    <FormModal
                        modalProps={{ ...lensCtaModalProps, width: 630, centered: false }}
                        footer={
                            <SidebarFooterButtons
                                dataTestIdPrefix="lens-cta"
                                onCancel={() => {
                                    setShowErrors(false);
                                    lensCtaModalProps.onCancel();
                                }}
                                apply={() => {
                                    if (isValid) {
                                        setShowErrors(false);
                                        void submitForm();
                                    } else {
                                        setShowErrors(true);
                                    }
                                }}
                                applyLoading={updateLensLoading}
                                isDisabled={!isValid}
                            />
                        }
                    >
                        <SidebarModalHeading>Lens Call-To-Action</SidebarModalHeading>
                        <SidebarModalInfo>
                            Lens Call-to-Action allows you to direct Snapchatters to your website or app.
                            {/** TODO Add Learn More Link */}
                        </SidebarModalInfo>
                        <Flexbox justifyContent="space-between" className={formContainerStyle}>
                            <LensCtaChild>
                                <LensCtaMediaPreview
                                    lensPreview={lensPreview}
                                    lensCtaText={
                                        values.type === MyLensesLensCtaType.CtaTypeUnset
                                            ? ""
                                            : LensCtaTextLabelMap[values.text]
                                    }
                                    lensIconURL={lensIconURL}
                                />
                            </LensCtaChild>
                            <LensCtaChild grow={1}>
                                <EditLensCtaForm setShowErrors={setShowErrors} showErrors={showErrors} />
                            </LensCtaChild>
                        </Flexbox>
                    </FormModal>
                )}
            </EditLensCtaFormik>
        </>
    );
};
